import useLangPrefix from '@/hooks/useLangPrefix'
import { stopEvent } from '@/utils/tools'
import { Pagination, PaginationProps } from '@gate/gui'
import { useRouter } from 'next/router'
import { FC } from 'react'
import { PaginationItem } from './style'
import { useTranslation } from 'next-i18next'
import { useStore } from '@/store/index'
import { PAGE_TYPE } from '@/constants/index'

const BasePagination: FC<PaginationProps & { routerName?: string }> = ({
  page = 1,
  total = 1,
  routerName = 'page',
  ...rest
}) => {
  const { locale } = useRouter()
  const { pageType } = useStore().globalStore
  const { langPrefix } = useLangPrefix()
  const { asPath } = useRouter()
  const { t } = useTranslation([pageType === PAGE_TYPE.COIN_DETAIL ? 'coin_details_v2' : 'price'])
  const pathname = asPath?.replace(/\?.*$/, '')

  return (
    <Pagination
      {...rest}
      page={page}
      total={total}
      {...({ component: 'ul' } as any)}
      itemComponent={props => {
        if (props.page === 'prev') {
          if (page <= 1) {
            return (
              <PaginationItem
                data-ar={String(locale === 'ar')}
                {...(props as any)}
                onClick={stopEvent}
                data-click={page <= 1}
              >
                <svg className="iconpark-icon size-16">
                  <use href="#v31-16px-line-zuojiantou"></use>
                </svg>
              </PaginationItem>
            )
          }

          return (
            <PaginationItem data-ar={String(locale === 'ar')} data-click={page <= 1}>
              <a
                {...(props as any)}
                href={`${langPrefix}${pathname}${Number(props.page) === 1 ? '' : `?${routerName}=${props.page}`}`}
                onClick={e => {
                  stopEvent(e)
                  if (page <= 1) {
                    return
                  }
                  props?.onClick?.()
                }}
                title={t('P.JumperTo') + (page - 1)}
                aria-label={t('P.JumperTo') + (page - 1)}
              >
                <svg className="iconpark-icon size-16">
                  <use href="#v31-16px-line-zuojiantou"></use>
                </svg>
              </a>
            </PaginationItem>
          )
        }
        if (props.page === 'dots') {
          return (
            <PaginationItem>
              <svg className="text-3" width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M5.31676 12.1577V13.5H3.94957V12.1577H5.31676ZM8.58434 12.1577V13.5H7.21715V12.1577H8.58434ZM11.8519 12.1577V13.5H10.4847V12.1577H11.8519Z"
                  fill="currentColor"
                ></path>
              </svg>
            </PaginationItem>
          )
        }
        if (props.page === 'next') {
          if (page >= total) {
            return (
              <PaginationItem
                data-ar={String(locale === 'ar')}
                {...(props as any)}
                onClick={stopEvent}
                data-click={page >= total}
              >
                <svg className="iconpark-icon size-16">
                  <use href="#v31-16px-line-youjiantou"></use>
                </svg>
              </PaginationItem>
            )
          }

          return (
            <PaginationItem data-ar={String(locale === 'ar')} data-click={page === total}>
              <a
                {...(props as any)}
                href={`${langPrefix}${pathname}${Number(props.page) === 1 ? '' : `?${routerName}=${props.page}`}`}
                onClick={e => {
                  stopEvent(e)
                  if (page === total) {
                    return
                  }
                  props?.onClick?.()
                }}
                title={t('P.JumperTo') + (page + 1)}
                aria-label={t('P.JumperTo') + (page + 1)}
              >
                <svg className="iconpark-icon size-16">
                  <use href="#v31-16px-line-youjiantou"></use>
                </svg>
              </a>
            </PaginationItem>
          )
        }

        return (
          <PaginationItem>
            <a
              {...(props as any)}
              href={`${langPrefix}${pathname}${Number(props.page) === 1 ? '' : `?${routerName}=${props.page}`}`}
              onClick={e => {
                stopEvent(e)
                props?.onClick?.()
              }}
              title={t('P.GoToPageIndex', { index: props.page })}
              aria-label={t('P.GoToPageIndex', { index: props.page })}
            >
              {props.page}
            </a>
          </PaginationItem>
        )
      }}
    />
  )
}

export default BasePagination
